/**
 * ExtractionOrchestrationPanel — extraction des garanties (passes MÉTIER).
 *
 * Chunk 9.AA.1b. Décision PO : **option C** — un composant dédié, dans son propre
 * fichier, inséré dans `ExtractionPanel` avec une intégration minimale. Une seule
 * page de soumission cohérente, une machine d'état nouvelle et isolée, et le
 * parcours d'identification existant strictement inchangé.
 *
 * Les deux fonctions ne font pas la même chose, et l'interface doit le dire :
 *   - l'extraction du bloc au-dessus **identifie** le document (assureur, langue,
 *     sections présentes) — c'est une lecture de structure, ouverte aux agents ;
 *   - ce panneau extrait le **contenu métier** : primes, garanties, conditions.
 *     Réservé aux humains autorisés, parce que chaque passe engage un appel payant.
 *
 * ## Reprise d'état après rafraîchissement
 *
 * Exigence PO explicite. Elle repose sur deux lectures au montage :
 *   - `GET /plan` expose l'orchestration `running`, s'il y en a une ;
 *   - `GET /` (listing, ajouté en 9.AA.1b) expose l'historique, **y compris les
 *     orchestrations terminées**.
 *
 * Sans le second, recharger la page effaçait le bilan que l'utilisateur venait de
 * lire, sans aucun moyen de le retrouver. L'orchestration affichée est donc :
 * celle qu'on vient de lancer, sinon celle en cours, sinon la plus récente.
 *
 * ## Ce que le panneau ne fait pas
 *
 * Il ne choisit pas le fichier : `ExtractionPanel` porte déjà le sélecteur, et en
 * ajouter un second créerait deux vérités sur le document analysé. Il reçoit donc
 * `fileId` et se contente de dire quoi faire quand il est absent.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// `lib/apiError` et non `lib/api` : le module pur, sans Supabase ni
// `window.localStorage` à l'import (doctrine 9.K.14). Ce panneau reste ainsi
// montable en test jsdom sans variables d'environnement Supabase.
import { ApiException } from "../../../lib/apiError";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import {
  isOrchestrationTerminal,
  orchestrationKeys,
  useExtractionOrchestration,
  useExtractionOrchestrations,
  useExtractionPlan,
  useStartExtractionOrchestration,
  type ExtractionOrchestration,
  type ExtractionPlanWire,
  type OrchestrationPass,
  type PlainSection,
  type RecognizedSection,
  type ReconciliationReport,
} from "../../../lib/queries/extractionOrchestrations";
import { extractionsKeys } from "../../../lib/queries/extractions";

import { LaunchOrchestrationDialog } from "./LaunchOrchestrationDialog";

const POLL_INTERVAL_MS = 2500;
const BASE = "submissions.detail.orchestration";

export type ExtractionOrchestrationPanelProps = {
  submissionId: string;
  /** Fichier choisi dans le sélecteur d'`ExtractionPanel`. `null` = aucun choix. */
  fileId: string | null;
};

export function ExtractionOrchestrationPanel(props: ExtractionOrchestrationPanelProps) {
  const { submissionId, fileId } = props;
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const qc = useQueryClient();

  const [confirmOpen, setConfirmOpen] = useState(false);
  /** Orchestration qu'on vient de lancer — prime sur tout le reste. */
  const [justLaunchedId, setJustLaunchedId] = useState<string | null>(null);

  const plan = useExtractionPlan(submissionId);
  const historique = useExtractionOrchestrations(submissionId);
  const start = useStartExtractionOrchestration();

  const activeId = plan.data?.active_orchestration?.id ?? null;
  const derniereId = historique.data?.items[0]?.id ?? null;
  const affichee = justLaunchedId ?? activeId ?? derniereId;

  // Sondage — remarque P2 Codex, fondée.
  //
  // La condition était `activeId || justLaunchedId`. Elle laissait un trou : si
  // `/plan` échoue ou est périmé alors que l'historique renvoie une orchestration
  // encore `running` (le cas d'un simple rechargement de page), les deux étaient
  // nuls, la requête de détail se créait SANS intervalle, et la progression restait
  // figée jusqu'à ce que l'utilisateur rafraîchisse à la main.
  //
  // On passe donc TOUJOURS l'intervalle et on laisse le hook décider : son
  // `refetchInterval` s'arrête de lui-même sur `done`/`partial`/`failed`. Une
  // orchestration close ne sera pas sondée pour autant — l'intervalle n'est
  // reprogrammé qu'après une réponse non terminale. La règle vit à un seul endroit
  // au lieu d'être dupliquée ici sous une forme incomplète.
  const detailBrut = useExtractionOrchestration(affichee ?? undefined, {
    pollIntervalMs: POLL_INTERVAL_MS,
  });
  const detail = detailBrut.data;

  // Au passage à un état terminal, deux caches sont périmés : le plan (dont
  // `active_orchestration` doit repasser à `null` pour réactiver le bouton) et les
  // extractions de la soumission (les champs consolidés viennent de changer).
  // Sans ça, l'utilisateur voit « en cours » sur un travail fini, et des valeurs
  // périmées en dessous.
  const dernierStatut = useRef<string | null>(null);
  useEffect(() => {
    const statut = detail?.status ?? null;
    if (statut && statut !== dernierStatut.current && isOrchestrationTerminal(detail?.status)) {
      void qc.invalidateQueries({ queryKey: orchestrationKeys.plan(submissionId) });
      void qc.invalidateQueries({ queryKey: orchestrationKeys.list(submissionId) });
      void qc.invalidateQueries({
        queryKey: extractionsKeys.byParent("submission", submissionId),
      });
      setJustLaunchedId(null);
    }
    dernierStatut.current = statut;
  }, [detail?.status, qc, submissionId]);

  const planWire = normaliserPlan(plan.data ?? detail);
  const passesALancer = normaliserPlan(plan.data)?.selected_passes ?? [];
  // Un `activeId` périmé ne doit pas masquer un détail terminal — remarque P2
  // Codex, fondée.
  //
  // Le détail sondé est la preuve la PLUS FRAÎCHE qu'une orchestration est finie.
  // La condition traitait pourtant tout `active_orchestration` en cache comme
  // parole d'évangile : si le refetch de `/plan` échoue ou tarde, React Query garde
  // l'ancien `activeId`, et le panneau continuait d'afficher « déjà en cours » en
  // retenant le bouton de relance alors que le travail était terminé.
  //
  // On n'accorde donc plus foi à `activeId` quand c'est exactement l'orchestration
  // qu'on affiche comme terminale.
  const detailEstTerminal = !!detail && isOrchestrationTerminal(detail.status);
  const activeIdFiable = activeId !== null && !(detailEstTerminal && activeId === detail?.id);
  const uneOrchestrationTourne = activeIdFiable || detail?.status === "running";

  // Le plan décrit UN document — celui sur lequel l'identification a lu les
  // sections. Si le courtier a depuis sélectionné un autre fichier, les passes
  // retenues décrivent le mauvais document (remarque P2 Codex). Le serveur refuse
  // déjà l'écart en 409 ; on l'empêche aussi ici, pour que l'utilisateur voie
  // pourquoi plutôt que de se heurter à une erreur après avoir confirmé.
  //
  // `identification_file_id` à `null` = aucune citation exploitable : on ne peut
  // prouver ni la correspondance ni l'écart, et refuser sur une absence de preuve
  // bloquerait des cas légitimes. On laisse alors passer, le serveur fait de même.
  const fichierDuPlan = plan.data?.identification_file_id ?? null;
  const ecartDeFichier = !!fileId && fichierDuPlan !== null && fichierDuPlan !== fileId;

  const peutLancer =
    !!fileId &&
    !!plan.data &&
    passesALancer.length > 0 &&
    !ecartDeFichier &&
    !uneOrchestrationTourne &&
    !start.isPending;

  // Le 409 « identification requise » n'est pas une panne, c'est une étape
  // manquante. On remplace donc le message serveur par une consigne d'interface
  // qui désigne le bouton juste au-dessus — l'utilisateur n'a pas à deviner où
  // aller. Tous les autres échecs gardent le message bilingue du serveur.
  const planErreur =
    plan.isError && plan.error instanceof ApiException
      ? plan.error.apiError?.code === "EXTRACTION_ORCHESTRATIONS_IDENTIFICATION_REQUIRED"
        ? t(`${BASE}.identification_required`)
        : getApiErrorMessage(plan.error, lang) || t("errors.generic")
      : plan.isError
        ? t("errors.generic")
        : null;

  const lancementErreur =
    start.error instanceof ApiException
      ? getApiErrorMessage(start.error, lang) || t("errors.generic")
      : start.isError
        ? t("errors.generic")
        : null;

  function onConfirm() {
    if (!fileId) return;
    start.mutate(
      { submission_id: submissionId, file_id: fileId },
      {
        onSuccess: (row) => {
          setJustLaunchedId(row.id);
          setConfirmOpen(false);
        },
        // Remarque P2 Codex, fondée : seul le succès était traité.
        //
        // Si un autre onglet — ou un autre courtier — lance l'orchestration entre le
        // chargement de ce plan et cette confirmation, le verrou serveur répond
        // `409 ALREADY_ACTIVE` **en nommant** l'orchestration active. Sans traitement,
        // les caches n'étaient pas invalidés et le rafraîchissement au focus est
        // désactivé : le panneau restait dans un état « lançable » et ne suivait
        // jamais l'orchestration qui tournait réellement.
        //
        // On adopte donc l'identifiant renvoyé — l'utilisateur voit immédiatement la
        // progression de celle qui tourne, ce qu'il voulait de toute façon — et on
        // rafraîchit plan et historique.
        onError: (err) => {
          if (
            err instanceof ApiException &&
            err.apiError?.code === "EXTRACTION_ORCHESTRATIONS_ALREADY_ACTIVE"
          ) {
            const details = err.apiError.details as { orchestration_id?: unknown } | undefined;
            const id =
              typeof details?.orchestration_id === "string" ? details.orchestration_id : null;
            if (id) setJustLaunchedId(id);
            void qc.invalidateQueries({ queryKey: orchestrationKeys.plan(submissionId) });
            void qc.invalidateQueries({ queryKey: orchestrationKeys.list(submissionId) });
            setConfirmOpen(false);
          }
        },
      },
    );
  }

  return (
    <section aria-labelledby="orchestration-title" className="mt-5 border-t border-slate-200 pt-4">
      <h3
        id="orchestration-title"
        className="text-sm font-medium uppercase tracking-wide text-slate-500"
      >
        {t(`${BASE}.title`)}
      </h3>
      <p className="mt-1 max-w-prose text-xs text-slate-500">{t(`${BASE}.subtitle`)}</p>

      {plan.isLoading && <p className="mt-3 text-sm text-slate-400">{t(`${BASE}.plan_loading`)}</p>}

      {planErreur && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-medium text-amber-900">{t(`${BASE}.plan_error_title`)}</p>
          <p className="mt-1 text-sm text-amber-900">{planErreur}</p>
        </div>
      )}

      {planWire && <PlanPreview plan={planWire} />}

      {/* Actions */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {uneOrchestrationTourne ? (
          <p
            aria-live="polite"
            className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700"
          >
            <span className="font-medium">{t(`${BASE}.already_active_title`)}</span>{" "}
            {t(`${BASE}.already_active_body`)}
          </p>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={!peutLancer}
            className="btn-primary focus-visible:ring-brand-500 touch-manipulation text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {detail && isOrchestrationTerminal(detail.status)
              ? t(`${BASE}.relaunch`)
              : t(`${BASE}.launch`)}
          </button>
        )}
        {!fileId && !planErreur && (
          <p className="text-xs text-slate-500">
            {t("submissions.detail.extraction.pick_file_hint")}
          </p>
        )}
      </div>

      {/* Écart plan / document — le bouton est désactivé, il faut dire pourquoi.
          Un bouton grisé sans explication est le pire des deux mondes. */}
      {ecartDeFichier && (
        <div aria-live="polite" className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-medium text-amber-900">{t(`${BASE}.file_mismatch_title`)}</p>
          <p className="mt-1 max-w-prose text-sm text-amber-900">
            {t(`${BASE}.file_mismatch_body`)}
          </p>
        </div>
      )}

      {detail && isOrchestrationTerminal(detail.status) && !uneOrchestrationTourne && (
        <p className="mt-2 max-w-prose text-xs text-slate-500">{t(`${BASE}.relaunch_hint`)}</p>
      )}

      {lancementErreur && !confirmOpen && (
        <p
          aria-live="polite"
          className="mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-700"
        >
          {lancementErreur}
        </p>
      )}

      {/* Progression + bilans.
          Même prudence que pour le plan : on n'affiche la progression que si la
          charge porte réellement les deux clés qu'elle lit. Une exception ici
          démonterait `ExtractionPanel` avec nous, et l'écran de soumission entier
          disparaîtrait — sélecteur de fichier et identification compris. */}
      {detail && Array.isArray(detail.passes) && detail.summary ? (
        <OrchestrationProgress orchestration={detail} />
      ) : (
        !plan.isLoading &&
        !historique.isLoading &&
        !planErreur && <p className="mt-4 text-sm text-slate-400">{t(`${BASE}.empty`)}</p>
      )}

      {confirmOpen && (
        <LaunchOrchestrationDialog
          passes={passesALancer}
          pending={start.isPending}
          errorText={lancementErreur}
          onConfirm={onConfirm}
          onClose={() => setConfirmOpen(false)}
        />
      )}
    </section>
  );
}

/**
 * Ramène une charge de plan à une forme sûre, ou `undefined`.
 *
 * ## Pourquoi cette fonction existe
 *
 * Ce panneau est monté **à l'intérieur** d'`ExtractionPanel`. Une exception lancée
 * pendant son rendu ne dégrade pas seulement l'extraction des garanties : React
 * démonte tout l'arbre parent, et **l'écran de soumission entier disparaît** —
 * sélecteur de fichier, identification, anomalies compris.
 *
 * Le défaut a été trouvé par les deux sentinelles d'`ExtractionPanel`, qui mockent
 * `api.get` par une réponse générique de listing (`{ items, next_cursor }`). Le hook
 * de plan résolvait donc avec un objet sans `selected_passes`, et
 * `plan.selected_passes.length` faisait tomber la page. Le scénario n'est pas
 * artificiel : n'importe quelle divergence de contrat, un proxy qui réécrit une
 * réponse, une route renommée, produirait le même effet en production.
 *
 * On ne fait pas confiance à la forme reçue, donc. Les quatre tableaux sont
 * coercés, le plafond a un défaut, et une charge qui n'a **aucune** des clés
 * attendues renvoie `undefined` — mieux vaut ne rien afficher qu'emporter l'écran.
 */
function normaliserPlan(
  brut: Partial<ExtractionPlanWire> | undefined,
): ExtractionPlanWire | undefined {
  if (!brut || typeof brut !== "object") return undefined;

  const tableau = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
  const aUneCle =
    "selected_passes" in brut ||
    "recognized_sections" in brut ||
    "skipped_passes" in brut ||
    "unknown_sections" in brut;
  if (!aUneCle) return undefined;

  return {
    recognized_sections: tableau<RecognizedSection>(brut.recognized_sections),
    recognized_sections_without_pass: tableau<PlainSection>(brut.recognized_sections_without_pass),
    unknown_sections: tableau<PlainSection>(brut.unknown_sections),
    selected_passes: tableau<string>(brut.selected_passes),
    skipped_passes: tableau<ExtractionPlanWire["skipped_passes"][number]>(brut.skipped_passes),
    max_business_passes:
      typeof brut.max_business_passes === "number" ? brut.max_business_passes : 6,
  };
}

// -----------------------------------------------------------------------------
// PlanPreview — les quatre catégories, sans rien écarter en silence
// -----------------------------------------------------------------------------

function PlanPreview({ plan }: { plan: ExtractionPlanWire }) {
  const { t } = useTranslation();

  return (
    <div className="mt-3 space-y-3">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
          {t(`${BASE}.passes_selected`)}
        </p>
        {plan.selected_passes.length === 0 ? (
          <p className="mt-1 text-sm text-slate-400">—</p>
        ) : (
          <ul className="mt-1 flex flex-wrap gap-1.5">
            {plan.selected_passes.map((p) => (
              <li
                key={p}
                className="bg-brand-50 text-brand-700 rounded-full px-2.5 py-1 text-xs font-medium"
              >
                {t(`${BASE}.pass_label.${p}`, { defaultValue: p })}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-1.5 max-w-prose text-xs text-slate-500">{t(`${BASE}.structural_hint`)}</p>
      </div>

      {plan.skipped_passes.length > 0 && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {t(`${BASE}.passes_skipped`)}
          </p>
          <ul className="mt-1 space-y-0.5">
            {plan.skipped_passes.map((s) => (
              <li key={s.pass} className="text-xs text-slate-600">
                <span className="font-medium">
                  {t(`${BASE}.pass_label.${s.pass}`, { defaultValue: s.pass })}
                </span>{" "}
                —{" "}
                {t(`${BASE}.skip_reason.${s.reason}`, {
                  max: plan.max_business_passes,
                  defaultValue: s.reason,
                })}
              </li>
            ))}
          </ul>
        </div>
      )}

      {plan.recognized_sections.length > 0 && (
        <SectionGroup
          label={t(`${BASE}.sections_recognized`)}
          sections={plan.recognized_sections}
          tone="neutre"
        />
      )}

      {plan.recognized_sections_without_pass.length > 0 && (
        <SectionGroup
          label={t(`${BASE}.sections_without_pass`)}
          hint={t(`${BASE}.sections_without_pass_hint`)}
          sections={plan.recognized_sections_without_pass}
          tone="attention"
        />
      )}

      {plan.unknown_sections.length > 0 && (
        <SectionGroup
          label={t(`${BASE}.sections_unknown`)}
          hint={t(`${BASE}.sections_unknown_hint`)}
          sections={plan.unknown_sections}
          tone="attention"
        />
      )}
    </div>
  );
}

/**
 * Un groupe de sections. `raw` est affiché tel quel — c'est la chaîne produite par
 * le modèle, celle qu'il faudra chercher dans les logs si une section surprend.
 * `translate="no"` empêche une traduction automatique du navigateur de brouiller
 * un identifiant technique.
 */
function SectionGroup(props: {
  label: string;
  hint?: string;
  sections: readonly (RecognizedSection | PlainSection)[];
  tone: "neutre" | "attention";
}) {
  const { label, hint, sections, tone } = props;
  const puce =
    tone === "attention"
      ? "bg-amber-50 text-amber-900 ring-1 ring-amber-200"
      : "bg-slate-100 text-slate-700";

  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <ul className="mt-1 flex flex-wrap gap-1.5">
        {sections.map((s) => (
          <li
            key={s.normalized || s.raw}
            translate="no"
            className={`rounded-full px-2.5 py-1 text-xs ${puce}`}
          >
            {s.raw}
          </li>
        ))}
      </ul>
      {hint && <p className="mt-1.5 max-w-prose text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

// -----------------------------------------------------------------------------
// OrchestrationProgress — issue globale + progression + bilan par passe
// -----------------------------------------------------------------------------

function OrchestrationProgress({ orchestration }: { orchestration: ExtractionOrchestration }) {
  const { t } = useTranslation();
  const { status, summary, passes } = orchestration;

  // `partial` a son propre traitement visuel. La décision PO est explicite : le
  // succès partiel est représenté explicitement, JAMAIS comme un succès global.
  const ton =
    status === "done"
      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
      : status === "partial"
        ? "border-amber-200 bg-amber-50 text-amber-900"
        : status === "failed"
          ? "border-red-200 bg-red-50 text-red-800"
          : "border-slate-200 bg-slate-50 text-slate-700";

  const corps =
    status === "partial"
      ? t(`${BASE}.outcome_partial_body`)
      : status === "failed"
        ? t(`${BASE}.outcome_failed_body`)
        : null;

  return (
    <div className="mt-4">
      <div aria-live="polite" className={`rounded-md border p-3 ${ton}`}>
        <p className="text-sm font-medium">{t(`${BASE}.outcome.${status}`)}</p>
        <p className="mt-0.5 text-xs tabular-nums">
          {t(`${BASE}.summary`, {
            done: summary.done,
            failed: summary.failed,
            total: summary.total,
          })}
        </p>
        {corps && <p className="mt-1.5 text-xs">{corps}</p>}
      </div>

      <h4 className="mt-4 text-xs font-medium uppercase tracking-wide text-slate-500">
        {t(`${BASE}.progress_title`)}
      </h4>
      <ul className="mt-2 space-y-2">
        {passes.map((p) => (
          <PassRow key={p.extraction_id} pass={p} />
        ))}
      </ul>
    </div>
  );
}

function PassRow({ pass }: { pass: OrchestrationPass }) {
  const { t } = useTranslation();
  const libelle = pass.pass_id
    ? t(`${BASE}.pass_label.${pass.pass_id}`, { defaultValue: pass.pass_id })
    : "—";

  const badge =
    pass.status === "done"
      ? "bg-emerald-100 text-emerald-800"
      : pass.status === "failed"
        ? "bg-red-100 text-red-800"
        : pass.status === "running"
          ? "bg-brand-100 text-brand-800"
          : "bg-slate-100 text-slate-600";

  return (
    <li className="rounded-md border border-slate-200 bg-white p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0 break-words text-sm font-medium text-slate-800">{libelle}</span>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge}`}>
          {t(`${BASE}.pass_status.${pass.status}`)}
        </span>
      </div>

      {pass.error_code && (
        <p translate="no" className="mt-1 text-xs text-red-700">
          {t(`submissions.detail.extraction.error_codes.${pass.error_code}`, {
            defaultValue: pass.error_code,
          })}
        </p>
      )}

      <ReconciliationSummary report={pass.reconciliation} passStatus={pass.status} />
    </li>
  );
}

/**
 * Bilan d'une passe.
 *
 * La distinction qui compte : `null` veut dire « réconciliation jamais atteinte »
 * (la passe a échoué avant), et **pas** « zéro changement ». Les confondre
 * affirmerait à l'utilisateur que rien n'a bougé alors que rien n'a été vérifié.
 */
function ReconciliationSummary(props: {
  report: ReconciliationReport | null;
  passStatus: OrchestrationPass["status"];
}) {
  const { report, passStatus } = props;
  const { t } = useTranslation();

  if (report === null) {
    if (passStatus === "failed") {
      return <p className="mt-2 text-xs text-slate-500">{t(`${BASE}.report_not_reached`)}</p>;
    }
    return null;
  }

  // Pas de `useMemo` : ce composant rend après les retours anticipés ci-dessus, et
  // un hook conditionnel violerait les règles des hooks. Le calcul est de toute
  // façon trivial — quatre comparaisons.
  //
  // On n'affiche que les compteurs non nuls : une ligne « 0 champ retiré » occupe
  // de la place pour ne rien dire. Si les quatre sont à zéro, le libellé « aucun
  // changement » le dit une seule fois.
  const compteurs = (
    [
      ["created", report.created],
      ["updated", report.updated],
      ["preserved_override", report.preserved_override],
      ["removed_or_invalidated", report.removed_or_invalidated],
    ] as const
  ).filter(([, n]) => n > 0);

  return (
    <div className="mt-2 border-t border-slate-100 pt-2">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
        {t(`${BASE}.report_title`)}
      </p>
      {compteurs.length === 0 ? (
        <p className="mt-1 text-xs text-slate-500">{t(`${BASE}.report_none`)}</p>
      ) : (
        <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
          {compteurs.map(([cle, n]) => (
            <li key={cle} className="text-xs text-slate-700">
              <span className="font-semibold tabular-nums">{n}</span> {t(`${BASE}.report.${cle}`)}
            </li>
          ))}
        </ul>
      )}

      {report.removed_field_keys.length > 0 && (
        <details className="mt-2">
          <summary className="focus-visible:ring-brand-500 cursor-pointer text-xs text-slate-600 focus-visible:outline-none focus-visible:ring-2">
            {t(`${BASE}.report_removed_label`)}
          </summary>
          <p className="mt-1 text-xs text-slate-500">{t(`${BASE}.report_removed_hint`)}</p>
          <ul className="mt-1 flex flex-wrap gap-1.5">
            {report.removed_field_keys.map((k) => (
              <li
                key={k}
                translate="no"
                className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-600"
              >
                {k}
              </li>
            ))}
          </ul>
        </details>
      )}

      {report.shared_stale_fields.length > 0 && (
        <details className="mt-2">
          <summary className="focus-visible:ring-brand-500 cursor-pointer text-xs text-slate-600 focus-visible:outline-none focus-visible:ring-2">
            {t(`${BASE}.report_shared_label`)}
          </summary>
          <p className="mt-1 text-xs text-slate-500">{t(`${BASE}.report_shared_hint`)}</p>
          <ul className="mt-1 flex flex-wrap gap-1.5">
            {report.shared_stale_fields.map((s) => (
              <li
                key={s.field_key}
                translate="no"
                className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-600"
              >
                {s.field_key}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
